import React, { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router'
import { ApolloProvider } from '@apollo/client'

import { ThemeProvider, StyleSheetManager } from 'styled-components'
import { IntlProvider } from 'react-intl'
import { Intercom, LiveChatLoaderProvider } from 'react-live-chat-loader'

import apolloClient from './apollo-client'
import shouldForwardProp from './utils/shouldForwardProp'

import theme from './theme'
import localeData from './locales'

import LazyComponent from './components/LazyComponent'
import ErrorBoundary from './containers/ErrorBoundary'

import FeedbackProvider from './providers/FeedbackProvider'
import SessionProvider from './providers/SessionProvider'

import Navigation from './components/Navigation'
import PageTitle from './components/PageTitle'

import OrganisationRoutes from './routes/Organisation'
import TeamMemberRoutes from './routes/TeamMember'
import SitesRoutes from './routes/Sites'
import SiteRoutes from './routes/Site'
import StandaloneRoutes from './routes/Standalone'
import UserRoutes from './routes/User'

const Setup = React.lazy(() => import('./containers/Setup'))

if ('serviceWorker' in navigator && new RegExp('https').test(document.location.protocol)) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js'))
}

const containerEl = document.getElementById('react-app')

const flashFeedback = el => {
  let initialFeedback = {}

  const alert = el.dataset.alert
  const notice = el.dataset.notice

  if (alert) {
    initialFeedback = { type: 'error', message: alert }
  } else if (notice) {
    initialFeedback = { type: 'success', message: notice }
  }

  return initialFeedback
}

const intercomId = el => el.dataset.intercom
const countryCode = el => el.dataset.country || 'en'

const renderApp = () => {
  if (containerEl) {
    createRoot(containerEl).render(
      <IntlProvider defaultLocale="en" locale={countryCode(containerEl)} messages={localeData}>
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
          <ThemeProvider theme={theme}>
            <LiveChatLoaderProvider provider="intercom" providerKey={intercomId(containerEl)}>
              <ApolloProvider client={apolloClient}>
                <Router
                  future={{
                    v7_relativeSplatPath: true,
                    v7_startTransition: true
                  }}
                >
                  <Suspense
                    fallback={
                      <>
                        <div
                          style={{
                            height: '60px',
                            backgroundColor: 'rgb(4,20,82)'
                          }}
                        />
                        <div style={{ height: 'calc(100vh - 60px - 48px)' }} />
                      </>
                    }
                  >
                    <ErrorBoundary>
                      <FeedbackProvider>
                        <PageTitle />
                        <SessionProvider>
                          <Navigation forceRefresh={false} feedback={flashFeedback(containerEl)} />
                          <Routes>
                            <Route path="/tests/:uuid/:shareToken/*" element={<StandaloneRoutes />} />
                            <Route path="/organisations/:orgId/*" element={<OrganisationRoutes />} />
                            <Route path="/teams/:teamId/team/*" element={<TeamMemberRoutes />} />
                            <Route path="/teams/:teamId" element={<Navigate to="sites" replace={true} />} />
                            <Route path="/teams/:teamId/sites/*" element={<SitesRoutes />} />
                            <Route path="/teams/:teamId/:siteId/*" element={<SiteRoutes />} />
                            <Route path="/you/*" element={<UserRoutes />} />
                            <Route
                              path="/setup/:orgId?"
                              element={
                                <LazyComponent>
                                  <Setup />
                                </LazyComponent>
                              }
                            />
                          </Routes>
                        </SessionProvider>
                      </FeedbackProvider>
                    </ErrorBoundary>
                  </Suspense>
                </Router>
              </ApolloProvider>
              <Intercom color="#3057F4" containerClass="intercom-wrapper" />
            </LiveChatLoaderProvider>
          </ThemeProvider>
        </StyleSheetManager>
      </IntlProvider>
    )
  }
}

renderApp()
